<i18n
  src="@/locales/views/organization/process/work_logs/show_process.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/harvest_team_work_log.yml" lang="yaml"></i18n>

<template>
  <go-back-link
    :text="$t('labels.go_back_link')"
    :name="routeName('list_processes')"
    :params="{ organization_id: organization_id }"
  />

  <bread-crumbs :labels="[module_name, harvest_process.name]" />

  <template v-if="load_data_general">
    <h1 class="page-title">
      <i class="fa-solid fa-crate-apple"></i>
      <span class="space-icon"></span>
      {{ $t("labels.title") }}
    </h1>

    <div class="mt-6 flex flex-row justify-end">
      <div class="space-x-3">
        <new-button
          @click.prevent="goToNewHarvestTeamWorkLog()"
          :label="$t('labels.new_button')"
        />
      </div>
    </div>
    <div class="mt-8 flex flex-col">
      <div class="table-first-container">
        <div class="table-second-container">
          <div class="table-third-container">
            <table class="table">
              <thead class="table-header">
                <tr>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.harvest_team_work_log.work_date") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.harvest_team_work_log.worker_count") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.harvest_team_work_log.worked_hours") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.harvest_team_work_log.productivity_ratio") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.harvest_team_work_log.harvest_team") }}
                  </th>
                  <th scope="col" class="table-header-cell"></th>
                </tr>
              </thead>
              <tbody class="table-body">
                <tr
                  class="table-row"
                  v-for="harvest_team_work_log in harvest_team_work_logs"
                  :key="harvest_team_work_log.id"
                >
                  <td class="table-cell">
                    {{ dateFormatter(harvest_team_work_log.work_date) }}
                  </td>
                  <td class="table-cell">
                    {{ harvest_team_work_log.worker_count }}
                  </td>
                  <td class="table-cell">
                    {{ harvest_team_work_log.worked_hours }}
                  </td>
                  <td class="table-cell">
                    {{ harvest_team_work_log.productivity_ratio }}
                  </td>
                  <td class="table-cell">
                    {{ harvest_team_work_log.harvest_team.harvest_team_code }}
                  </td>
                  <td class="table-cell-action space-x-3">
                    <destroy-button
                      v-if="!harvest_team_work_log.proc_button_destroy"
                      :disabled_button="false"
                      @destroyEvent="
                        destroyHarvestTeamWorkLog(harvest_team_work_log)
                      "
                      :label="$t('labels.destroy_button_label')"
                      :title="
                        $t('labels.destroy_button_title', {
                          day: dateFormatter(harvest_team_work_log.work_date),
                        })
                      "
                      :message="$t('labels.destroy_button_message')"
                      :label_confirm="$t('labels.destroy_button_label_confirm')"
                    />
                    <destroy-button-processing
                      v-else
                      :label="$t('labels.destroy_button_label')"
                    />
                    <edit-button
                      @click.prevent="
                        goToEditHarvestTeamWorkLog(harvest_team_work_log)
                      "
                      :label="$t('labels.edit_button')"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    <table-paginator
      v-model="current_page"
      :total_rows="total_harvest_team_work_logs"
      :rows_per_page="rows_per_page"
    />
  </template>

  <loading-message v-else />
</template>

<script>
import Requester from "@/views/organization/requesters/process/work_logs/show_process.js";
import ManageRequester from "@/views/organization/requesters/process/work_logs/manage_harvest_team_work_log.js";
import TablePaginator from "@/components/views/layout/TablePaginator.vue";
export default {
  name: "OrgProcessWorkLogsShowProcess",
  components: { TablePaginator },
  data() {
    return {
      load_data_general: false,
      current_page: 1,
      total_harvest_team_work_logs: 0,
      rows_per_page: 20,
      harvest_process: {},
      harvest_team_work_logs: [],
    };
  },
  props: {
    organization_id: { required: true },
    harvest_process_id: { required: true },
  },
  methods: {
    loadData: async function () {
      await this.fetchHarvestProcess();
      await this.fetchHarvestTeamWorkLogsTotal();
      await this.fetchHarvestTeamWorkLogs();
      this.load_data_general = true;
    },

    fetchHarvestProcess: async function () {
      let response = await Requester.fetchHarvestProcess(
        this.organization_id,
        this.harvest_process_id
      );
      this.harvest_process = response.data;
    },

    fetchHarvestTeamWorkLogsTotal: async function () {
      let response = await Requester.fetchHarvestTeamWorkLogsTotal(
        this.organization_id,
        this.harvest_process_id
      );
      this.total_harvest_team_work_logs = response.data;
    },

    fetchHarvestTeamWorkLogs: async function () {
      let response = await Requester.fetchHarvestTeamWorkLogs(
        this.organization_id,
        this.harvest_process_id,
        this.current_page - 1,
        this.rows_per_page
      );
      this.harvest_team_work_logs = response.data;
    },

    destroyHarvestTeamWorkLog: async function (harvest_team_work_log) {
      try {
        harvest_team_work_log.proc_button_destroy = true;
        let response = await ManageRequester.destroy(
          this.organization_id,
          this.harvest_process_id,
          harvest_team_work_log.id
        );
        this.registerSuccessNotification({
          title: response.data.success_message,
        });
        this.harvest_team_work_logs.splice(
          this.harvest_team_work_logs.indexOf(harvest_team_work_log),
          1
        );
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields,
          });
        }
      } finally {
        harvest_team_work_log.proc_button_destroy = false;
      }
    },

    goToNewHarvestTeamWorkLog: function () {
      this.$router.push({
        name: this.routeName("new_harvest_team_work_log"),
        params: {
          organization_id: this.organization_id,
          harvest_process_id: this.harvest_process_id,
        },
      });
    },

    goToEditHarvestTeamWorkLog(harvest_team_work_log) {
      this.$router.push({
        name: this.routeName("edit_harvest_team_work_log"),
        params: {
          organization_id: this.organization_id,
          harvest_process_id: this.harvest_process_id,
          harvest_team_work_log_id: harvest_team_work_log.id,
        },
      });
    },
  },
  watch: {
    current_page: function () {
      this.fetchHarvestTeamWorkLogs();
    },
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          organization_id: this.organization_id,
        },
      }).then(() => this.loadData())
    );
  },
};
</script>
